import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaLayerGroup, FaBook, FaCheckSquare, FaPaperclip, FaServer, FaTerminal, FaFileAlt, FaRegStickyNote, FaCodeBranch, FaBookOpen, FaListOl } from "react-icons/fa";
import { SUBPROJECTS_CONSTANTS } from "../_MODULES/_API";
import { SUBPROJECT_TABS } from "../_MODULES/_Navigation";
import { STATUS_MAP_SUBPROJECT } from "../../../_Catalogs.Views";
import { PanelLogbook } from "../_UI/PanelLogbook";
import { PanelTodos } from "../_UI/PanelTodos";
import { PanelDrafts } from "../_UI/PanelDrafts";
import { PanelChanges } from "../_UI/PanelChanges";
import { ProjectLockScreen } from "../_UI/ProjectLockScreen";
import { useProjectAccess } from "../_Hooks/useProjectAccess";
import { PanelDocuments } from "../_UI/PanelDocuments";
import { PanelEnvironments } from "../_UI/PanelEnvironments";
import { PanelSnippets } from "../_UI/PanelSnippets";
import { PanelReadme } from "../_UI/PanelReadme";
import { PanelGuides } from "../_UI/PanelGuides";
import { PanelSteps } from "../_UI/PanelSteps";
import { PageHeader } from "../../../../../_CORE/APP/Templates/_UI/PageHeader/_index";
import { StatusBadge } from "../../../../../_CORE/APP/Templates/_UI/Status/_index";
import { EmptyState, Spinner, Chip } from "../../../../../_CORE/APP/Templates/_UI/Kit/_index";
import { useApiQuery } from "../../../../../_CORE/APP/Hooks/Hooks/_Records/_useApiQuery";
import { ViewLayout } from "../../../../_UI/ViewLayout";
import "../_UI/_Styles/Projects.css";

const TAB_ICONS = {
    logbook: <FaBook />,
    drafts: <FaRegStickyNote />,
    changes: <FaCodeBranch />,
    tasks: <FaCheckSquare />,
    environments: <FaServer />,
    scripts: <FaTerminal />,
    steps: <FaListOl />,
    files: <FaPaperclip />,
    readme: <FaFileAlt />,
    guides: <FaBookOpen />,
};
const TABS = SUBPROJECT_TABS.map((item) => ({ ...item, icon: TAB_ICONS[item.value] }));

const TAB_DEFAULT = TABS[0].value;

const onResolveTab = (tab) => (TABS.some((item) => item.value === tab) ? tab : TAB_DEFAULT);

export const SubProjectDetailViewMain = () => {

    const { id, subId, tab } = useParams();

    const navigate = useNavigate();

    const ACTIVE_TAB = onResolveTab(tab);

    const { useAccessPin, onUnlock } = useProjectAccess(id);

    const TABS_LINKED = TABS.map((item) => ({ ...item, to: `/projects/${id}/subprojects/${subId}/${item.value}` }));

    const { apiGet: onGetSubProject, useApiData: SUB_PROJECT, useApiLoading: IS_LOADING, mutateLocal } = useApiQuery({
        minTimeQuery: 300, enablePagination: false, startLoading: true,
    });

    const onLoadSubProject = () => onGetSubProject({
        endpoint: SUBPROJECTS_CONSTANTS.DETAIL, isDetail: true, config: { pathParams: { id: subId } },
    });

    useEffect(() => { onLoadSubProject(); }, [subId]); // eslint-disable-line react-hooks/exhaustive-deps

    if (IS_LOADING) return <Spinner label="Cargando subproyecto…" />;

    if (!SUB_PROJECT) return <EmptyState icon={<FaLayerGroup />} title="No encontramos ese subproyecto" />;

    if (SUB_PROJECT.project?.hasAccessPin && !useAccessPin) {
        return (
            <ProjectLockScreen projectId={id} projectName={SUB_PROJECT.project?.name}
                backTo={`/projects/${id}/subprojects`} onUnlocked={onUnlock} />
        );
    }

    return (
        <ViewLayout
            header={
                <PageHeader
                title={SUB_PROJECT.name}
                description={SUB_PROJECT.description}
                icon={<span className="LStudio__app_view_color_icon"
                    style={{ "--LS__item_color": SUB_PROJECT.color || "var(--LS__app_priv_accent)" }}><FaLayerGroup /></span>}
                backTo={`/projects/${id}/subprojects`}
                isSticky
                status={<StatusBadge status={SUB_PROJECT.type} map={STATUS_MAP_SUBPROJECT} />}
                breadcrumbs={[
                    { label: "Proyectos", href: "/projects", onClick: (event) => { event.preventDefault(); navigate("/projects"); } },
                    {
                        label: SUB_PROJECT.project?.name ?? "Proyecto", href: `/projects/${id}/subprojects`,
                        onClick: (event) => { event.preventDefault(); navigate(`/projects/${id}/subprojects`); },
                    },
                    { label: SUB_PROJECT.name },
                ]}
                tabs={TABS_LINKED}
                    activeTab={ACTIVE_TAB}
                    onChangeTab={(value) => navigate(`/projects/${id}/subprojects/${subId}/${value}`)} />
            } >

            {
                SUB_PROJECT.stack?.length > 0 && (
                    <div className="LStudio__app_project_stack">
                        {SUB_PROJECT.stack.map((tech) => <Chip key={tech} tone="neutral">{tech}</Chip>)}
                    </div>
                )
            }

            <div className="LStudio__app_project_panel">

                {ACTIVE_TAB === "logbook" && <PanelLogbook subProjectId={subId} />}

                {ACTIVE_TAB === "drafts" && <PanelDrafts subProjectId={subId} />}

                {ACTIVE_TAB === "changes" && <PanelChanges subProjectId={subId} projectId={id} />}

                {ACTIVE_TAB === "tasks" && <PanelTodos subProjectId={subId} projectId={id} />}

                {ACTIVE_TAB === "environments" && <PanelEnvironments subProjectId={subId} />}

                {ACTIVE_TAB === "scripts" && <PanelSnippets subProjectId={subId} />}

                {ACTIVE_TAB === "steps" && <PanelSteps projectId={id} subProjectId={subId} scopeRecord={SUB_PROJECT} onScopePatched={(patch) => mutateLocal({ id: SUB_PROJECT.id, patch })} />}

                {ACTIVE_TAB === "files" && <PanelDocuments subProjectId={subId} />}

                {ACTIVE_TAB === "readme" && <PanelReadme subProject={SUB_PROJECT}
                    onSaved={(saved) => mutateLocal({ id: SUB_PROJECT.id, patch: saved })} />}

                {ACTIVE_TAB === "guides" && <PanelGuides projectId={id} subProjectId={subId} scopeName={SUB_PROJECT.name} />}

            </div>
        </ViewLayout>
    );
};
